import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';

import { IMembershipList, IMingaFeatureToggleKeys, MembershipListFlags, MembershipListType } from 'libs/domain';
import { combineLatest, map, ReplaySubject, takeUntil } from 'rxjs';

import { RootService } from '@app/src/app/minimal/services/RootService';
import { FlexTimeManagerRoutes } from '@app/src/app/modules/flex-time-manager/constants';
import { HpmRoutes } from '@app/src/app/modules/hallpass-manager/hpm.constants';
import { ListMembershipService } from '@app/src/app/services/ListMembership';
import {
  MODAL_OVERLAY_DATA,
  ModalOverlayRef,
  ModalOverlayServiceCloseEventType,
} from '@app/src/app/shared/components/modal-overlay';
import { MingaSettingsService } from '@app/src/app/store/Minga/services';

import { CrudFormBase } from '../crud-form-base/crud-form-base.abstract';
import { markNestedFormGroupAsDirty } from '../crud-form-base/crud-form-base.utils';
import { SystemAlertCloseEvents, SystemAlertModalService, SystemAlertModalType } from '../system-alert-modal';
import {
  createForm,
  MembershipListKeys,
  NoPartyGroupEditMessage,
  setForm,
} from './constants/no-party-group-edit.constants';
import { NoPartyGroupEditModalData } from './types/no-party-group-edit.types';

@Component({
  selector: 'mg-no-party-group-edit',
  templateUrl: './no-party-group-edit.component.html',
  styleUrls: ['./no-party-group-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoPartyGroupEditComponent extends CrudFormBase<IMembershipList> implements OnInit, OnDestroy {
  // Constants

  public readonly MSG = NoPartyGroupEditMessage;
  public readonly FLAGS = MembershipListFlags;
  public readonly FORM_FIELDS = MembershipListKeys;

  public readonly form = this._fb.group(createForm());

  // Observables

  private readonly _destroyedSubject = new ReplaySubject<void>(1);

  public readonly multipleModulesEnabled$ = combineLatest([
    this._mingaSettings.getModuleEnabledObs(IMingaFeatureToggleKeys.HALLPASS_ENABLED),
    this._mingaSettings.getModuleEnabledObs(IMingaFeatureToggleKeys.FLEX_TIME_ENABLED),
  ]).pipe(
    takeUntil(this._destroyedSubject),
    map(([hallPassEnabled, flexTimeEnabled]) => {
      return hallPassEnabled && flexTimeEnabled;
    }),
  );

  // Component Constructor

  constructor(
    @Inject(MODAL_OVERLAY_DATA) public dialogData: NoPartyGroupEditModalData,
    private _modalOverlay: ModalOverlayRef,
    private _fb: UntypedFormBuilder,
    private _membershipService: ListMembershipService,
    private _systemAlertModal: SystemAlertModalService,
    private _rootService: RootService,
    private _mingaSettings: MingaSettingsService,
    private _router: Router,
  ) {
    super({
      id: dialogData?.list?.id,
      get: async () => dialogData?.list,
      create: async data => this._membershipService.updateMembershipList(data),
      update: async data => this._membershipService.updateMembershipList(data),
      delete: async data => {
        await this._membershipService.deleteMembershipList(data.id);
        return data;
      },
      onSetForm: (data, isNew) => {
        if (isNew) {
          // when creating a new no party group, the checkbox associated to the module
          // you opened the modal from should be enabled by default
          const defaultModule = this._getDefaultModule();
          this.form.get(MembershipListKeys.FLAGS).setValue(defaultModule || []);
          return;
        }

        setForm(data, this.form);
      },
      onDelete: async () => {
        const modalRef = await this._systemAlertModal.open({
          modalType: SystemAlertModalType.WARNING,
          heading: NoPartyGroupEditMessage.DELETE_CONFIRM_TITLE,
          closeBtn: NoPartyGroupEditMessage.CLOSE_BUTTON_LABEL,
          confirmActionBtn: NoPartyGroupEditMessage.DELETE_BUTTON_LABEL,
          message: NoPartyGroupEditMessage.DELETE_CONFIRM_MESSAGE,
        });
        const response = await modalRef.afterClosed().toPromise();
        return response?.type === SystemAlertCloseEvents.CONFIRM;
      },
      onSubmit: () => {
        return {
          name: this.form.get(MembershipListKeys.NAME).value,
          flags: this.form.get(MembershipListKeys.FLAGS).value || [],
          listType: MembershipListType.NO_PARTY,
          active: true,
          id: this.dialogData?.list?.id,
        };
      },
      onShowLoader: promise => this._rootService.addLoadingPromise(promise),
      onCancel: async () => this._modalOverlay.close(ModalOverlayServiceCloseEventType.CLOSE),
      onSuccess: type => {
        if (type === ModalOverlayServiceCloseEventType.CREATE) {
          this._modalOverlay.close(ModalOverlayServiceCloseEventType.CREATE);
        } else if (type === ModalOverlayServiceCloseEventType.DELETE) {
          this._modalOverlay.close(ModalOverlayServiceCloseEventType.DELETE);
        } else {
          this._modalOverlay.close(ModalOverlayServiceCloseEventType.SUBMIT);
        }
      },
      onValidate: () => {
        markNestedFormGroupAsDirty(this.form);
        return this.form.valid;
      },
    });
  }

  // Lifecycle Hooks

  ngOnInit() {
    this.init();
  }

  ngOnDestroy() {
    this._destroyedSubject.next();
    this._destroyedSubject.complete();
  }

  // Public Methods

  public selectActiveModule(active: boolean, flag: MembershipListFlags) {
    const current = this.form.get(MembershipListKeys.FLAGS).value || [];
    if (active) {
      const updated = [...current, flag];
      this.form.get(MembershipListKeys.FLAGS).setValue(updated);
    } else {
      this.form.get(MembershipListKeys.FLAGS).setValue(current.filter(m => m !== flag));
    }
  }

  public isModuleActive(flag: MembershipListFlags): boolean {
    const current = this.form.get(MembershipListKeys.FLAGS).value || [];
    return current.includes(flag);
  }

  // Private Methods

  /**
   * Determine where we opened the modal from in order to set the default module in the form
   */
  private _getDefaultModule(): MembershipListFlags[] {
    const url = this._router.url;
    const modules: MembershipListFlags[] = [];

    if (url.includes('/' + FlexTimeManagerRoutes.ROOT)) modules.push(MembershipListFlags.FLEX_TIME_ENABLED);
    if (url.includes('/' + HpmRoutes.ROOT)) modules.push(MembershipListFlags.HALL_PASS_ENABLED);

    return modules;
  }
}
